import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  departments: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const departmentSlice = createSlice({
  name: 'department',
  initialState,
  reducers: {
    setDepartments: (state, action) => {
      state.departments = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setDepartments, setTotalCount, setTotalPages, setLoading, setError } = departmentSlice.actions;

// Get departments (paginated & searchable)
export const getDepartments = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const search = filters?.search || '';
    const response = await axiosInstance.get('/admin/department/list', {
      params: { page, limit, search },
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || result.length;
      const totalPages = response.data.data?.totalPages || 1;
      dispatch(setDepartments(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setDepartments([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch departments';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setDepartments([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch departments';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single department details
export const getDepartmentDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/department/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch department details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch department details';
    return { status: false, message: errorMsg };
  }
};

// Add new department
export const addDepartment = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/department/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add department';
    return { status: false, message: errorMsg };
  }
};

// Update department details
export const updateDepartment = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/department/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update department';
    return { status: false, message: errorMsg };
  }
};

// Delete department
export const deleteDepartment = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/department/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete department' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete department';
    return { status: false, message: errorMsg };
  }
};

export default departmentSlice.reducer;
